// src/views/student/StudentListView/StudentListView.tsx
/* eslint-disable max-len */
import React, {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  List, Avatar, Skeleton, Divider, Button, Input, Tooltip,
} from 'antd';
import {
  TbChartBar, TbEdit, TbUserPlus, TbUsers, TbUsersPlus,
} from 'react-icons/tb';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import AddStudentModal from '../AddStudentModal';
import { getListOfStudents, getIsLoadingStudentsList, getStudentGroups } from '../slice/selectors';
import { fetchStudentGroups, fetchStudentsPage, refreshStudentsPage } from '../slice/actions';
import styles from './StudentListView.module.less';
import DeleteStudentModal from '../DeleteStudentModal';
import { StudentGroup, UserData } from '../../../services/ApiService.types';
import { ACTIVITY_ENABLED } from '../../../services/activity';
import ActivityModal from '../ActivityModal';
import EditStudentModal from '../EditStudentModal/EditStudentModal';
import ListAccessStudentModal from '../ListAccessStudentModal/ListAccessStudentModal';
import ResendActivationModal from '../ResendActivationModal';
import { StudentLinkKind } from '../ResendActivationModal/ResendActivationModal';
import StudentGroupModal from '../StudentGroupModal';
import { getStudentGroupNames, sortGroupsByName } from '../../../utils/studentGroups';
import { matchesSearch, toSearchWords } from '../../../utils/search';
import { ReactComponent as Thrash } from '../../../assets/icons/teacher/trash.svg';
import { ReactComponent as Edit } from '../../../assets/icons/teacher/edit.svg';
import { ReactComponent as ListAccess } from '../../../assets/icons/teacher/list-access.svg';
import { ReactComponent as Mail } from '../../../assets/icons/teacher/mail.svg';
import { ReactComponent as Key } from '../../../assets/icons/teacher/key.svg';
import { pluralForm } from '../../../utils/plural';

// Wybór w kolumnie grup: wszyscy, uczniowie spoza wszystkich grup albo numer grupy
type GroupFilter = 'all' | 'ungrouped' | number;

const StudentListView: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [editedStudent, setEditedStudent] = useState<UserData | null>(null);
  const [deletedStudent, setDeletedStudent] = useState<UserData | null>(null);
  const [listAccessStudent, setListAccessStudent] = useState<UserData | null>(null);
  // Okienko koperty albo klucza. Ucznia bierzemy z listy po numerze, żeby po edycji miało świeży adres
  const [linkModal, setLinkModal] = useState<{ studentId: number, kind: StudentLinkKind } | null>(null);
  // Uczeń, do którego okienka klucza wracamy po „Popraw adres”
  const [passwordLinkReturnId, setPasswordLinkReturnId] = useState<number | null>(null);
  const [activityStudent, setActivityStudent] = useState<UserData | null>(null);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const loading = useSelector(getIsLoadingStudentsList);
  const list = useSelector(getListOfStudents);
  const linkStudent: UserData | undefined = linkModal
    ? list.find((student: UserData) => student.id === linkModal.studentId)
    : undefined;

  const groups = useSelector(getStudentGroups);
  const sortedGroups = useMemo(() => sortGroupsByName(groups), [groups]);
  const hasGroups = sortedGroups.length > 0;
  // Po każdym wejściu na ekran „Wszyscy”. Grupa usunięta albo jeszcze niewczytana działa jak „Wszyscy”
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const selectedGroup = typeof groupFilter === 'number'
    ? sortedGroups.find((group) => group.id === groupFilter) ?? null
    : null;
  // „Bez grupy” stoi w kolumnie grup — gdy ostatnia grupa zniknie, razem z kolumną znika i ten wybór
  const showUngrouped = groupFilter === 'ungrouped' && hasGroups;
  // Okno grupy: `undefined` — zamknięte, `null` — nowa grupa
  const [groupModal, setGroupModal] = useState<StudentGroup | null | undefined>(undefined);

  const [search, setSearch] = useState('');
  const searchWords = useMemo(() => toSearchWords(search), [search]);

  // Uczniowie należący do jakiejkolwiek grupy — reszta to „Bez grupy”
  const groupedIds = useMemo(() => new Set(groups.flatMap((group) => group.studentIds)), [groups]);
  const ungroupedCount = useMemo(
    () => list.filter((student: UserData) => !groupedIds.has(student.id)).length,
    [list, groupedIds],
  );

  // Wybór w kolumnie zawęża listę: do członków grupy albo do uczniów spoza grup. Szukanie — jak w oknie grupy:
  // w imieniu, nazwisku i adresie
  const visibleStudents: UserData[] = useMemo(() => list.filter((student: UserData) => {
    if (selectedGroup && !selectedGroup.studentIds.includes(student.id)) {
      return false;
    }

    if (showUngrouped && groupedIds.has(student.id)) {
      return false;
    }

    return matchesSearch(`${student.name} ${student.surname} ${student.email}`, searchWords);
  }), [list, selectedGroup, showUngrouped, groupedIds, searchWords]);

  // Etykieta wybranej grupy stałaby w każdym wierszu — przy uczniach zostają ich pozostałe grupy
  const tagGroups = useMemo(() => (
    selectedGroup ? sortedGroups.filter((group) => group.id !== selectedGroup.id) : sortedGroups
  ), [sortedGroups, selectedGroup]);

  // Nagłówek nad uczniami: nazwa wybranej grupy albo „Bez grupy” / „Wszyscy”, z liczbą uczniów
  let headerName = t('student:list.all');
  let headerCount = list.length;

  if (selectedGroup) {
    headerName = selectedGroup.name;
    headerCount = selectedGroup.studentIds.length;
  } else if (showUngrouped) {
    headerName = t('student:list.ungrouped');
    headerCount = ungroupedCount;
  }

  // Pusta lista: najpierw szukanie, potem pusta grupa albo pusta „Bez grupy”. Bez nich nauczyciel nie ma jeszcze uczniów
  let emptyText = t('student:list.empty.noStudents');

  if (searchWords.length > 0) {
    if (selectedGroup) {
      emptyText = t('student:list.empty.noMatchInGroup', { group: selectedGroup.name });
    } else if (showUngrouped) {
      emptyText = t('student:list.empty.noMatchUngrouped');
    } else {
      emptyText = t('student:list.empty.noMatch');
    }
  } else if (selectedGroup) {
    emptyText = t('student:list.empty.emptyGroup');
  } else if (showUngrouped && list.length > 0) {
    emptyText = t('student:list.empty.empty');
  }

  const hideGroupModal = useCallback(() => setGroupModal(undefined), []);
  // Po zapisaniu widać zapisaną grupę — przy nowej to od razu sprawdzenie, kto do niej trafił
  const onGroupSaved = useCallback((group: StudentGroup) => {
    setGroupModal(undefined);
    setGroupFilter(group.id);
  }, []);
  const onGroupDeleted = useCallback(() => {
    setGroupModal(undefined);
    setGroupFilter('all');
  }, []);

  // Nowego ucznia ma być widać, a szukanie się czyści. Uczeń spoza wybranej grupy przenosi ekran na „Wszyscy”;
  // przy „Bez grupy” zostaje, bo nowy uczeń bez grupy właśnie tam jest
  const onStudentAdded = useCallback((isInGroup: boolean) => {
    setSearch('');

    if (!isInGroup) {
      setGroupFilter((prev) => (prev === 'ungrouped' ? prev : 'all'));
    }
  }, []);

  useEffect(() => {
    dispatch(fetchStudentGroups());
  }, [dispatch]);

  const hideActivityModal = useCallback(() => setActivityStudent(null), []);
  const showActivityModal = useCallback((student: UserData) => () => setActivityStudent(student), []);

  const loadMoreData = useCallback(() => {
    if (loading) {
      return;
    }
    dispatch(fetchStudentsPage());
  }, [dispatch, loading]);

  const hideListAccessStudentModal = useCallback(() => setListAccessStudent(null), [setListAccessStudent]);
  const showListAccessStudentModal = useCallback((student: UserData) => () => setListAccessStudent(student), [setListAccessStudent]);

  // Edycja otwarta z okienka klucza wraca do niego — i po zapisaniu, i po anulowaniu
  const hideEditModal = useCallback(() => {
    setEditedStudent(null);

    if (passwordLinkReturnId !== null) {
      setLinkModal({ studentId: passwordLinkReturnId, kind: 'password' });
      setPasswordLinkReturnId(null);
    }
  }, [passwordLinkReturnId]);
  const showEditModal = useCallback((student: UserData) => () => setEditedStudent(student), [setEditedStudent]);

  const hideDeleteModal = useCallback(() => setDeletedStudent(null), [setDeletedStudent]);
  const showDeleteModal = useCallback((student: UserData) => () => setDeletedStudent(student), [setDeletedStudent]);

  const hideLinkModal = useCallback(() => setLinkModal(null), []);
  const showLinkModal = useCallback((student: UserData, kind: StudentLinkKind) => () => (
    setLinkModal({ studentId: student.id, kind })
  ), []);
  // „Popraw adres” przechodzi do edycji tego samego ucznia. Przy kopercie zmiana adresu sama wysyła nowy
  // link, więc okienko się kończy. Przy kluczu niczego nie wysyła — po edycji wracamy, już z nowym adresem.
  const editLinkStudentAddress = useCallback(() => {
    if (!linkModal || !linkStudent) {
      return;
    }

    setEditedStudent(linkStudent);
    setPasswordLinkReturnId(linkModal.kind === 'password' ? linkModal.studentId : null);
    setLinkModal(null);
  }, [linkModal, linkStudent]);

  const showAddModal = () => {
    setIsModalVisible(true);
  };

  const hideAddModal = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    // Inteligentne ładowanie - bez flickeringu
    if (list.length === 0) {
      // Pierwsze wejście - pokaż loading
      dispatch(fetchStudentsPage());
    } else {
      // Mamy dane - silent refresh w tle
      dispatch(refreshStudentsPage());
    }
  }, [dispatch, list.length]);

  return (
    <>
      <AddStudentModal
        isVisible={isModalVisible}
        group={selectedGroup}
        hideModal={hideAddModal}
        onAdded={onStudentAdded}
      />
      {listAccessStudent && (
      <ListAccessStudentModal
        isVisible={!!listAccessStudent}
        student={listAccessStudent}
        hideModal={hideListAccessStudentModal}
      />
      )}
      {editedStudent && <EditStudentModal isVisible={!!editedStudent} student={editedStudent} hideModal={hideEditModal} />}
      {deletedStudent && <DeleteStudentModal student={deletedStudent} hideModal={hideDeleteModal} />}
      {activityStudent && <ActivityModal student={activityStudent} hideModal={hideActivityModal} />}
      {linkModal && linkStudent && (
        <ResendActivationModal
          student={linkStudent}
          kind={linkModal.kind}
          hideModal={hideLinkModal}
          onEditAddress={editLinkStudentAddress}
        />
      )}
      {groupModal !== undefined && (
        <StudentGroupModal
          group={groupModal}
          students={list}
          hideModal={hideGroupModal}
          onSaved={onGroupSaved}
          onDeleted={onGroupDeleted}
        />
      )}
      <div className={styles.wrapper}>
        {/* Pasek: dodanie ucznia i grupy, z prawej szukanie. Na telefonie przyciski z samą ikoną, napis w dymku.
            Ikona przy prawej krawędzi, jak na pasku ekranu list — dlatego jako dziecko przycisku, nie przez `icon` */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarButtons}>
            <Tooltip title={t('student:button.create')} overlayClassName={styles.labelTooltip}>
              <Button
                onClick={showAddModal}
                className={classNames(styles.grayButton, styles.addStudentButton)}
              >
                <span className={styles.buttonLabel}>{t('student:button.create')}</span>
                <TbUserPlus className={styles.toolbarIcon} />
              </Button>
            </Tooltip>
            <Tooltip title={t('student:groups.new')} overlayClassName={styles.labelTooltip}>
              <Button
                onClick={() => setGroupModal(null)}
                className={styles.grayButton}
              >
                <span className={styles.buttonLabel}>{t('student:groups.new')}</span>
                <TbUsersPlus className={styles.toolbarIcon} />
              </Button>
            </Tooltip>
          </div>
          <Input.Search
            aria-label={t('student:list.search')}
            className={styles.searchInput}
            placeholder={t('student:list.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
        </div>

        {/* Bez grup sam panel uczniów, jak przed grupami. Z grupami z lewej kolumna grup, a nad uczniami nagłówek */}
        <div className={classNames(styles.columns, { [styles.withGroups]: hasGroups })}>
          {/* Wybór grupy zawęża listę obok do jej członków. Na węższym ekranie kolumna staje się rzędem pigułek nad listą */}
          {hasGroups && (
            <div className={styles.groupsPanel}>
              {/* Tej samej wysokości co nagłówek nad uczniami, więc kreski pod nimi idą jednym poziomem. Przy wielu grupach
                  przewijają się same wiersze, nagłówek stoi */}
              <div className={styles.groupsHeader}>{t('student:list.groups')}</div>
              <div className={styles.groupsList} role="group" aria-label={t('student:list.groups')}>
                <button
                  type="button"
                  className={classNames(styles.groupRow, styles.allRow, { [styles.groupRowActive]: !selectedGroup && !showUngrouped })}
                  aria-pressed={!selectedGroup && !showUngrouped}
                  onClick={() => setGroupFilter('all')}
                >
                  <span className={styles.groupRowName}>{t('student:list.all')}</span>
                  <span className={styles.groupRowCount}>{list.length}</span>
                </button>
                {/* Uczniowie, którzy nie są w żadnej grupie — bez tego widać ich tylko w „Wszyscy”, wśród reszty */}
                <button
                  type="button"
                  className={classNames(styles.groupRow, styles.allRow, { [styles.groupRowActive]: showUngrouped })}
                  aria-pressed={showUngrouped}
                  onClick={() => setGroupFilter('ungrouped')}
                >
                  <span className={styles.groupRowName}>{t('student:list.ungrouped')}</span>
                  <span className={styles.groupRowCount}>{ungroupedCount}</span>
                </button>
                <div className={styles.groupsSeparator} />

                {sortedGroups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    title={group.name}
                    className={classNames(styles.groupRow, { [styles.groupRowActive]: group.id === selectedGroup?.id })}
                    aria-pressed={group.id === selectedGroup?.id}
                    onClick={() => setGroupFilter(group.id)}
                  >
                    <span className={styles.groupRowName}>{group.name}</span>
                    <span className={styles.groupRowCount}>{group.studentIds.length}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.studentsPanel}>
            {hasGroups && (
              <div className={classNames(styles.panelHeader, { [styles.groupHeader]: selectedGroup })}>
                <span className={styles.headerName} title={selectedGroup?.name}>
                  {headerName}
                </span>
                <span className={styles.headerCount}>
                  {t(`student:list.count.${pluralForm(headerCount)}`, { n: headerCount })}
                </span>
                {/* Ikona przy prawej krawędzi, jak na przyciskach paska — dlatego jako dziecko przycisku, nie przez `icon` */}
                {selectedGroup && (
                  <Tooltip title={t('student:groups.edit')} overlayClassName={styles.labelTooltip}>
                    <Button
                      onClick={() => setGroupModal(selectedGroup)}
                      className={styles.editGroupButton}
                    >
                      {/* Na telefonie samo „Edytuj” — reszta napisu w dymku */}
                      <span className={styles.buttonLabel}>{t('student:groups.editShort')}<span className={styles.editGroupLabelRest}>{t('student:groups.editRest')}</span></span>
                      <TbEdit className={styles.editGroupIcon} />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}
            <div
              id="scrollableDiv"
              className={styles.listWrapper}
            >
              {/* Pusty stan jak na ekranie list: ikona i napis na środku panelu. W trakcie pierwszego wczytania nic — nie miga */}
              {visibleStudents.length === 0 && !loading && (
              <div className={styles.emptyState}>
                <TbUsers className={styles.emptyIcon} />
                <p>{emptyText}</p>
              </div>
              )}
              {visibleStudents.length > 0 && (
              <InfiniteScroll
                dataLength={visibleStudents.length}
                next={loadMoreData}
                hasMore={false}
                loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
                endMessage={<Divider />}
                scrollableTarget="scrollableDiv"
              >
                <List
                  dataSource={visibleStudents}
                  renderItem={(item: UserData) => (
                    <List.Item key={item.id} className={styles.listPoint}>
                      <List.Item.Meta
                        avatar={(
                          <Avatar className={classNames(styles.avatar, { [styles.avatarInactive]: item.isActive === false })}>
                            {item.name[0].toUpperCase()}{item.surname[0].toUpperCase()}
                          </Avatar>
                        )}
                        title={<span className={styles.name}>{item.name} {item.surname}</span>}
                        description={(
                          <>
                            <span className={styles.email}>{item.email}</span>
                            {/* Tylko jawne `false` — bez tego pola (stary backend) nic się nie pokazuje */}
                            {item.isActive === false && <span className={styles.inactive}>{t('student:list.inactive')}</span>}
                          </>
                        )}
                      />
                      {/* Poza nazwiskiem, żeby stały na środku wysokości wiersza, a nie przy jego górnej linii */}
                      <div className={styles.groupTags}>
                        {getStudentGroupNames(tagGroups, item.id).map((groupName) => (
                          <Tooltip key={groupName} title={t('student:list.belongsTo', { group: groupName })}>
                            <span className={styles.groupTag}>{groupName}</span>
                          </Tooltip>
                        ))}
                      </div>
                      <div className={styles.actions}>

                        {ACTIVITY_ENABLED && (
                          <div
                            className={styles.switchActive}
                            tabIndex={0}
                            role="button"
                            onClick={showActivityModal(item)}
                            onKeyPress={showActivityModal(item)}
                          >
                            <Tooltip
                              title={t('student:list.actions.activity')}
                              aria-labelledby={t('student:list.actions.activity')}
                              overlayClassName={styles.actionTooltip}
                            >
                              {/* Wielkość, kolor i grubość kreski jak w pozostałych ikonach wiersza (pliki SVG) */}
                              <span> <TbChartBar className={styles.icon} size="1.5rem" color="orange" strokeWidth={1.5} /> </span>
                            </Tooltip>
                          </div>
                        )}

                        {item.isActive === false && (
                          <div
                            className={styles.switchActive}
                            tabIndex={0}
                            role="button"
                            onClick={showLinkModal(item, 'activation')}
                            onKeyPress={showLinkModal(item, 'activation')}
                          >
                            <Tooltip
                              title={t('student:list.actions.resendActivation')}
                              aria-labelledby={t('student:list.actions.resendActivation')}
                              overlayClassName={styles.actionTooltip}
                            >
                              <span> <Mail className={styles.icon} /> </span>
                            </Tooltip>
                          </div>
                        )}

                        {/* W miejscu koperty, żeby kolumny ikon zostały równe */}
                        {item.isActive === true && (
                          <div
                            className={styles.switchActive}
                            tabIndex={0}
                            role="button"
                            onClick={showLinkModal(item, 'password')}
                            onKeyPress={showLinkModal(item, 'password')}
                          >
                            <Tooltip
                              title={t('student:list.actions.passwordLink')}
                              aria-labelledby={t('student:list.actions.passwordLink')}
                              overlayClassName={styles.actionTooltip}
                            >
                              <span> <Key className={styles.icon} /> </span>
                            </Tooltip>
                          </div>
                        )}

                        <div
                          className={styles.switchActive}
                          tabIndex={0}
                          role="button"
                          onClick={showListAccessStudentModal(item)}
                          onKeyPress={showListAccessStudentModal(item)}
                        >
                          <Tooltip
                            title={t('student:list.actions.sharedLists')}
                            aria-labelledby={t('student:list.actions.sharedLists')}
                            overlayClassName={styles.actionTooltip}
                          >
                            <span> <ListAccess className={styles.icon} /> </span>
                          </Tooltip>
                        </div>

                        <div
                          className={styles.switchActive}
                          tabIndex={0}
                          role="button"
                          onClick={showEditModal(item)}
                          onKeyPress={showEditModal(item)}
                        >
                          <Tooltip
                            title={t('student:list.actions.edit')}
                            aria-labelledby={t('student:list.actions.edit')}
                            overlayClassName={styles.actionTooltip}
                          >
                            <span> <Edit className={styles.icon} /> </span>
                          </Tooltip>
                        </div>

                        <div
                          className={classNames(styles.switchActive, styles.deleteAction)}
                          tabIndex={0}
                          role="button"
                          onClick={showDeleteModal(item)}
                          onKeyPress={showDeleteModal(item)}
                        >
                          <Tooltip
                            title={t('student:list.actions.delete')}
                            aria-labelledby={t('student:list.actions.delete')}
                            overlayClassName={styles.actionTooltip}
                          >
                            <span> <Thrash className={styles.icon} /> </span>
                          </Tooltip>
                        </div>

                      </div>
                    </List.Item>
                  )}
                />
              </InfiniteScroll>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentListView;
